<template>
	<div>
		<h5 class="text-muted">
			{{ broadcastHistoryText }}
		</h5>
		<div v-if="!broadcastsData">
			<p>
				{{ noBroadcastMessage }}
			</p>
		</div>
		<div v-else>
			<div class="panel panel-plain">
				<template v-for="(broadcast, index) in broadcastsData">
					<div class="panel-heading" :key="`${index}-heading`">
						<p class="panel-title text-black">
							<strong>{{ broadcast.BROADCAST_TITLE }}</strong>
							<span class="float-right">
								{{ broadcast.CREATED_DATE | datetimecapital }}
							</span>
						</p>
					</div>
					<div class="panel-body" :key="`${index}-body`">
						<!-- Phone Call Broadcast Message start -->
						<template v-if="!!Object.keys(broadcast.DETAILS.VOICE).length">
							<p>
								<span class="text-teal">
									<strong>
										<font-awesome-icon icon="phone"></font-awesome-icon>&emsp;{{ voiceCallText }}
									</strong>
								</span>
								<span :class="`float-right text-${broadcast.DETAILS.VOICE.CALL_STATUS === 'completed' ? 'green' : 'danger'}`">
									<font-awesome-icon :icon="`${broadcast.DETAILS.VOICE.CALL_STATUS === 'completed' ? 'check' : 'times'}`">
									</font-awesome-icon>
									{{ broadcast.DETAILS.VOICE.CALL_STATUS | capitalize }}
								</span>
							</p>
							<p>
								<strong>
									{{ phoneText }}
								</strong>
								{{ broadcast.DETAILS.VOICE.CALL_TO }}
								&emsp;
								<strong>
									{{ durationsText }}
								</strong>
								{{ broadcast.DETAILS.VOICE.CALL_DURATION ? broadcast.DETAILS.VOICE.CALL_DURATION : '-' }}
								&emsp;
								<strong>
									{{ answeredByText }}
								</strong>
								{{ broadcast.DETAILS.VOICE.CALL_ANSWERED_BY === 'human' ? broadcast.DETAILS.VOICE.CALL_ANSWERED_BY : '-' | capitalize }}
								<span class="pointer pull-right" @click="showHideVoiceDetails(index)">
									{{ !!showVoiceBroadcastsDetails[index] ? 'Hide' : 'Show' }}
									<font-awesome-icon icon="chevron-down" v-if="!showVoiceBroadcastsDetails[index]"></font-awesome-icon>
									<font-awesome-icon icon="chevron-up" v-else></font-awesome-icon>
								</span>
							</p>
							<div class="row" v-show="!!showVoiceBroadcastsDetails[index]">
								<div class="col-xs-12">
									<strong>
										{{ phoneMessageText }}
									</strong>
									<button class="btn btn-default"
													@click="displayTranslatedVoiceText(index)"
													v-if="!!broadcast.DETAILS.MESSAGES.TRANSLATED_VOICE_MESSAGE">
										<font-awesome-icon icon="plus"></font-awesome-icon> {{ showEnglishText }}
									</button>
								</div>
								<div class="col-xs-12 well margin-top-5">
									<span v-html="broadcast.DETAILS.MESSAGES.VOICE_MESSAGE"></span>
									<template v-if="!!broadcast.DETAILS.MESSAGES.AUDIO_FILE">
										<br />
										<audio controls
													v-for="(audio, audioIndex) in broadcast.DETAILS.MESSAGES.AUDIO_FILE"
													:key="`audio_player_${audioIndex}`"
													:src="audio.AUDIO_URL">
											{{ noAudioSupportText }}
										</audio>
									</template>
									<template v-if="!!broadcast.DETAILS.MESSAGES.TRANSLATED_VOICE_MESSAGE">
										<hr />
										<span v-html="broadcast.DETAILS.MESSAGES.TRANSLATED_VOICE_MESSAGE"></span>
									</template>
								</div>
							</div>
						</template>
						<!-- Phone Call Broadcast Message end -->
						<!-- SMS Broadcast Message start -->
						<template v-if="!!Object.keys(broadcast.DETAILS.SMS).length">
							<p>
								<span class="text-teal">
									<strong>
										<font-awesome-icon icon="sms"></font-awesome-icon>
										<!-- TODO: This part should be rethought so that way we can somehow pass the SMS_STATUS and text itself -->
										&emsp;
										{{ broadcast.DETAILS.SMS.SMS_STATUS | capitalize }}
										{{ smsTextMessage }}
									</strong>
								</span>
								<span :class="`float-right text-${broadcast.DETAILS.SMS.SMS_STATUS === 'sent' ? 'green' : 'danger'}`">
									<font-awesome-icon :icon="`${broadcast.DETAILS.SMS.SMS_STATUS ? 'check' : 'times'}`">
									</font-awesome-icon>
									{{ broadcast.DETAILS.SMS.SMS_STATUS | capitalize }}
								</span>
							</p>
							<p>
								<strong>
									{{ phoneText }}
								</strong>
								{{ broadcast.DETAILS.SMS.SMS_TO }}
								<span class="pointer pull-right" @click="showHideSmsDetails(index)">
									{{ !!showSmsBroadcastsDetails[index] ? 'Hide' : 'Show' }}
									<font-awesome-icon icon="chevron-down" v-if="!showSmsBroadcastsDetails[index]"></font-awesome-icon>
									<font-awesome-icon icon="chevron-up" v-else></font-awesome-icon>
								</span>
							</p>
							<div class="row" v-show="!!showSmsBroadcastsDetails[index]">
								<div class="col-xs-12">
									<strong>
										{{ smsTestMessageColon }}
									</strong>
									<button class="btn btn-default"
													@click="displayTranslatedSmsText(index)"
													v-if="!!broadcast.DETAILS.MESSAGES.TRANSLATED_SMS_MESSAGE">
										<font-awesome-icon icon="plus"></font-awesome-icon> {{ showEnglishText }}
									</button>
								</div>
								<div class="col-xs-12 well margin-top-5">
									<span v-html="broadcast.DETAILS.MESSAGES.SMS_MESSAGE"></span>
									<template v-if="!!broadcast.DETAILS.MESSAGES.TRANSLATED_SMS_MESSAGE">
										<hr />
										<span v-html="broadcast.DETAILS.MESSAGES.TRANSLATED_SMS_MESSAGE"></span>
									</template>
								</div>
							</div>
						</template>
						<!-- SMS Broadcast Message end -->
						<!-- Email Broadcast Message start -->
						<template v-if="!!Object.keys(broadcast.DETAILS.EMAIL).length">
							<p>
								<span class="text-teal">
									<strong>
										<font-awesome-icon icon="envelope"></font-awesome-icon>
										&emsp;
										{{ emailAllCaps }}
									</strong>
								</span>
								<span :class="`float-right text-${broadcast.DETAILS.EMAIL.EMAIL_STATUS === 'sent' ? 'green' : 'danger'}`">
									<font-awesome-icon :icon="`${broadcast.DETAILS.EMAIL.EMAIL_STATUS  === 'sent' ? 'check' : 'times'}`">
									</font-awesome-icon>
									{{ broadcast.DETAILS.EMAIL.EMAIL_STATUS | capitalize }}
								</span>
							</p>
							<p>
								<strong>
									{{ emailWithColon }}
								</strong>
								{{ broadcast.DETAILS.EMAIL.EMAIL_TO }}
								<span class="pointer pull-right" @click="showHideEmailDetails(index)">
									{{ !!showEmailBroadcastsDetails[index] ? 'Hide' : 'Show' }}
									<font-awesome-icon icon="chevron-down" v-if="!showEmailBroadcastsDetails[index]"></font-awesome-icon>
									<font-awesome-icon icon="chevron-up" v-else></font-awesome-icon>
								</span>
							</p>
							<div class="row" v-show="!!showEmailBroadcastsDetails[index]">
								<div class="col-xs-12">
									<strong>
										{{ subjectLineWithColon }}
									</strong> {{ broadcast.DETAILS.MESSAGES.MESSAGE_SUBJECT }}
									<button class="btn btn-default"
													@click="displayTranslatedEmailText(index)"
													v-if="!!broadcast.DETAILS.MESSAGES.TRANSLATED_EMAIL_MESSAGE">
										<font-awesome-icon icon="plus"></font-awesome-icon> {{ showEnglishText }}
									</button>
								</div>
								<div class="col-xs-12 well margin-top-5">
									<span v-html="broadcast.DETAILS.MESSAGES.EMAIL_MESSAGE"></span>
									<template v-if="!!broadcast.DETAILS.MESSAGES.TRANSLATED_EMAIL_MESSAGE">
										<hr />
										<span v-html="broadcast.DETAILS.MESSAGES.TRANSLATED_EMAIL_MESSAGE">
										</span>
									</template>
								</div>
							</div>
						</template>
						<!-- Email Broadcast Message end -->
						<hr />
					</div>
				</template>
			</div>
		</div>
	</div>
</template>

<script>
	import { capitalize } from '../../filters/Strings/capitalize.js';
	import { datetimecapital } from '../../filters/DateTime/datetimecapital.js';

	import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
	import '../../imports/fontAwesome.js';

	export default {
		name: 'BroadcastTab',
		props: {
			broadcastsData: {
				type: Array,
				default: () => {
					return [];
				}
			},
			broadcastHistoryText: {
				type: String,
				default: 'Broadcast History'
			},
			noBroadcastMessage: {
				type: String,
				default: 'No Broadcasts were found within the last 3 months'
			},
			voiceCallText: {
				type: String,
				default: 'Voice Call'
			},
			phoneText: {
				type: String,
				default: 'Phone:'
			},
			answeredByText: {
				type: String,
				default: 'Answered by:'
			},
			durationsText: {
				type: String,
				default: 'Duration:'
			},
			phoneMessageText: {
				type: String,
				default: 'Phone Message:'
			},
			showEnglishText: {
				type: String,
				default: 'Show English'
			},
			noAudioSupportText: {
				type: String,
				default: 'Your browser does not support the audio element.'
			},
			smsTextMessage: {
				type: String,
				default: "SMS Text Message"
			},
			smsTestMessageColon: {
				type: String,
				default: 'SMS Text Message:'
			},
			emailAllCaps: {
				type: String,
				default: 'EMAIL'
			},
			emailWithColon: {
				type: String,
				default: 'Email:'
			},
			subjectLineWithColon: {
				type: String,
				default: 'Subject Line:'
			}
		},
		data() {
			return {
				showEmailBroadcastsDetails: {},
				showVoiceBroadcastsDetails: {},
				showSmsBroadcastsDetails: {},
				showTranslatedSmsText: {},
				showTranslatedVoiceText: {},
				showTranslatedEmailText: {},
			};
		},
		components: {
			FontAwesomeIcon
		},
		filters: {
			capitalize,
			datetimecapital
		},
		methods: {
			showHideSmsDetails(index) {
				let show = !!this.showSmsBroadcastsDetails[index] ? false : true;

				this.$set(this.showSmsBroadcastsDetails, index, show);
			},
			showHideVoiceDetails(index) {
				let show = !!this.showVoiceBroadcastsDetails[index] ? false : true;

				this.$set(this.showVoiceBroadcastsDetails, index, show);
			},
			showHideEmailDetails(index) {
				let show = !!this.showEmailBroadcastsDetails[index] ? false : true;

				this.$set(this.showEmailBroadcastsDetails, index, show);
			},
			displayTranslatedSmsText(index) {
				let show = !!this.showTranslatedSmsText[index] ? false : true;

				this.$set(this.showTranslatedSmsText, index, show);
			},
			displayTranslatedVoiceText(index) {
				let show = !!this.showTranslatedVoiceText[index] ? false : true;

				this.$set(this.showTranslatedVoiceText, index, show);
			},
			displayTranslatedEmailText(index) {
				let show = !!this.showTranslatedEmailText[index] ? false : true;

				this.$set(this.showTranslatedEmailText, index, show);
			}
		}
	}
</script>
